import { useState, useCallback } from 'react';
import { DragStartEvent, DragOverEvent, DragEndEvent } from '@dnd-kit/core';
import { TaskItem } from '../types/dashboardTypes';
import { logger } from '../../../utils/logger';
import { Types } from '../dnd/taskTypes';
import { normalizeTaskDeadline } from '../../../lib/project-utils';
import { DashboardSection } from '../../../hooks/queries/dashboardTypes';
import { useUpdateDashboardTask } from '../../../hooks/queries/useDashboard';

interface DragState {
  sectionType: DashboardSection | null;
}

/**
 * Custom hook to handle drag and drop operations for dashboard tasks using React Query
 * Implements specific behaviors for each dashboard section:
 * - Today: Set deadline to today
 * - This Week: Show date picker for selecting a date this week
 * - Highlighted: Set task as highlighted
 * - Overdue: Prevent dropping (read-only section)
 */
export function useDashboardDnd(tasks: TaskItem[]) {
  // State for tracking the active drag operation
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [activeTask, setActiveTask] = useState<TaskItem | null>(null);
  const [dragOverSectionType, setDragOverSectionType] = useState<DashboardSection | null>(null);
  const [dragState, setDragState] = useState<DragState>({ sectionType: null });

  // State for date picker when dropping on "This Week" section
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [taskForDatePicker, setTaskForDatePicker] = useState<TaskItem | null>(null);

  // Get update mutation from React Query
  const updateDashboardTask = useUpdateDashboardTask();

  /**
   * Get today's date properly normalized for task deadlines
   */
  const getTodayISOString = useCallback(() => {
    const today = new Date();

    // Use our shared utility to normalize the date in a timezone-safe way
    const normalizedToday = normalizeTaskDeadline(today);

    logger.component('Getting normalized today for task deadline', {
      rawToday: today.toISOString(),
      normalizedToday,
    });

    return normalizedToday;
  }, []);

  /**
   * Reset all drag state
   */
  const resetDragState = useCallback(() => {
    setActiveTaskId(null);
    setActiveTask(null);
    setDragOverSectionType(null);
    setDragState({ sectionType: null });

    // Log when drag state is reset
    logger.component('Reset drag state');
  }, []);

  /**
   * Handle the start of a drag operation
   */
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;

      // Defensive check to ensure active and data.current exists
      if (!active || !active.data || !active.data.current) {
        logger.error('Invalid drag event', { event });
        return;
      }

      // We only need to check if we have a data object with type
      if (!active.data?.current?.type || active.data.current.type !== Types.TASK) {
        return;
      }

      const taskId = active.id as string;
      const task = tasks.find(t => t.id === taskId);

      if (!task) {
        logger.error('Task not found for drag', { taskId });
        return;
      }

      setActiveTaskId(taskId);
      setActiveTask(task);
    },
    [tasks]
  );

  /**
   * Handle dragging over a dashboard section
   */
  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;

      if (!over) return;

      // Defensive check for active data
      if (!active || !active.data || !active.data.current) {
        logger.error('Invalid drag over event', { event });
        return;
      }

      // We only need to check if we have a data object with type
      if (!active.data?.current?.type || active.data.current.type !== Types.TASK) {
        return;
      }

      // Get over target data
      const overId = over.id as string;

      // Check if this is one of our known section IDs
      const isKnownSection = ['today', 'thisWeek', 'highlighted', 'overdue'].includes(overId);
      let sectionType: DashboardSection | null = null;

      if (isKnownSection) {
        // Direct match to a known section ID
        sectionType = overId as DashboardSection;
      } else {
        // It's a task ID - find the task and get its dashboardSection property
        const overTask = tasks.find(t => t.id === overId);

        if (overTask && overTask.dashboardSection) {
          // Use the dashboardSection property directly
          sectionType = overTask.dashboardSection;
        } else {
          // If we can't find the section, log and continue
          logger.error(
            `Task missing dashboardSection: ${overId} - ${overTask?.title || 'Unknown'}`
          );
          return;
        }
      }

      setDragOverSectionType(sectionType);
      setDragState({ sectionType });
    },
    [tasks]
  );

  /**
   * Handle date selection from the date picker
   */
  const handleDateSelection = useCallback(
    async (date: Date | null) => {
      if (!taskForDatePicker || !date) {
        setShowDatePicker(false);
        setTaskForDatePicker(null);
        return;
      }

      // Use the utility function to normalize the date for task deadlines
      const normalizedDeadline = normalizeTaskDeadline(date);

      // Log the date normalization for debugging
      logger.component('Date selection normalized for task deadline', {
        originalDate: date.toISOString(),
        normalizedDeadline,
        taskId: taskForDatePicker.id,
      });

      try {
        // Create updates object with the normalized deadline
        const updates: Partial<TaskItem> = {
          deadline: normalizedDeadline,
        };

        // If coming from a highlighted section, also remove the highlighted status
        if (taskForDatePicker.dashboardSection === 'highlighted') {
          updates.is_highlighted = false;
        }

        // Update the task using the React Query mutation
        await updateDashboardTask.mutateAsync({
          taskId: taskForDatePicker.id,
          updates
        });
      } catch (error) {
        logger.error('Failed to update task with selected date', {
          error: error instanceof Error ? error.message : String(error),
          taskId: taskForDatePicker.id,
        });
      } finally {
        // Close the date picker
        setShowDatePicker(false);
        setTaskForDatePicker(null);
      }
    },
    [taskForDatePicker, updateDashboardTask]
  );

  /**
   * Handle the end of a drag operation (dropping)
   */
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!active) {
        logger.error('No active item in drag end event');
        resetDragState();
        return;
      }

      // First, extract active task information
      const taskId = active.id as string;

      logger.component('Dashboard drag end event', {
        taskId,
        activeTaskId,
        hasActiveTask: !!activeTask,
        overId: over?.id,
      });

      // If there's no over target or no active task, just clean up
      if (!over || !activeTask) {
        resetDragState();
        return;
      }

      // Defensive check for data property
      if (!active.data) {
        logger.error('No data in active item for drag end', { taskId });
        resetDragState();
        return;
      }

      try {
        // Get over target data - either a section or a task
        const overId = over.id as string;

        // Check if this is a direct drop on a section
        const isSection = ['today', 'thisWeek', 'highlighted', 'overdue'].includes(overId);
        let sectionType: DashboardSection | null = null;

        // HANDLING DIRECT DROP ON A SECTION
        if (isSection) {
          // Direct drop on a section - use its ID as section type
          sectionType = overId as DashboardSection;
          logger.component('Direct drop on section', {
            taskId,
            sectionType,
            overId,
          });
        }
        // HANDLING DROP ON A TASK
        else {
          // Find the task and use its dashboardSection property directly
          const overTask = tasks.find(t => t.id === overId);

          if (overTask && overTask.dashboardSection) {
            sectionType = overTask.dashboardSection;
          } else {
            // Simple error message for easy reading in logs
            logger.error(
              `Task missing dashboardSection: ${overId} - ${overTask?.title || 'Unknown'}`
            );

            // Exit with error
            resetDragState();
            return;
          }
        }

        // If we're dropping onto the Overdue section, don't allow it
        if (sectionType === 'overdue') {
          logger.component('Drop attempt on overdue section prevented', { taskId });
          resetDragState();
          return;
        }

        // If we're dropping a task onto its current section, just cancel (no need to update)
        if (activeTask && activeTask.dashboardSection === sectionType) {
          logger.component('Task dropped on same section, no update needed', {
            taskId,
            sectionType,
          });
          resetDragState();
          return;
        }

        // Handle section-specific logic - with proper async/await handling
        switch (sectionType) {
          case 'today': {
            // Set the task's deadline to today and clear highlighted status if needed
            const todayDate = getTodayISOString();

            // Log the task update intention
            logger.component('Setting task deadline to today', {
              taskId,
              taskTitle: activeTask?.title,
              todayDate,
              currentDeadline: activeTask?.deadline,
            });

            try {
              // When moving to Today, we need to update both deadline and turn off highlighting
              const updates: Partial<TaskItem> = {
                deadline: todayDate,
                // Explicitly set highlighted to false if moving from highlighted section
                is_highlighted:
                  activeTask?.dashboardSection === 'highlighted'
                    ? false
                    : activeTask?.is_highlighted,
              };

              // Use the React Query mutation to update the task
              await updateDashboardTask.mutateAsync({
                taskId,
                updates
              });
            } catch (error) {
              logger.error('Failed to set deadline to today', {
                error: error instanceof Error ? error.message : String(error),
                taskId,
              });
            }
            break;
          }

          case 'thisWeek':
            // If we're coming from the highlighted section, first turn off highlighting
            if (activeTask?.dashboardSection === 'highlighted') {
              try {
                await updateDashboardTask.mutateAsync({
                  taskId,
                  updates: { is_highlighted: false }
                });
              } catch (error) {
                logger.error('Failed to remove highlighted status', {
                  error: error instanceof Error ? error.message : String(error),
                  taskId,
                });
              }
            }

            // Show date picker to select a date within the week
            setTaskForDatePicker(activeTask);
            setShowDatePicker(true);
            logger.component('Opening date picker for This Week');
            break;

          case 'highlighted':
            // Mark the task as highlighted
            try {
              // Use the React Query mutation to update the task
              await updateDashboardTask.mutateAsync({
                taskId,
                updates: { is_highlighted: true }
              });
            } catch (error) {
              logger.error('Failed to mark task as highlighted', {
                error: error instanceof Error ? error.message : String(error),
                taskId,
              });
            }
            break;
        }
      } catch (error) {
        logger.error('Error in drag end handler', {
          error: error instanceof Error ? error.message : String(error),
          taskId,
        });
      } finally {
        // Always clean up the drag state no matter what happens
        resetDragState();
      }
    },
    [activeTask, activeTaskId, resetDragState, getTodayISOString, updateDashboardTask, tasks]
  );

  return {
    // Drag state
    activeTaskId,
    activeTask,
    dragOverSectionType,
    dragState,

    // Date picker state
    showDatePicker,
    setShowDatePicker,
    taskForDatePicker,

    // Event handlers
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDateSelection,
    resetDragState,
  };
}